// ═══════════════════════════════════════════════════════════════
// FirebaseCompatAdapter.js — StorageAdapter for Firebase RTDB compat mode
// For apps using firebase.database() (CDN/compat), NOT modular imports
// ═══════════════════════════════════════════════════════════════

import { StorageAdapter } from '../HelpCenterConfig.js';

/**
 * Firebase Realtime Database adapter using the compat API.
 *
 * Usage:
 *   const adapter = new FirebaseCompatAdapter(window.firebaseDb);
 *
 * All paths are prefixed with 'data/' to match ArguMentor's structure.
 * Override `pathPrefix` in options to change this for other apps.
 */
class FirebaseCompatAdapter extends StorageAdapter {
  constructor(db, options = {}) {
    super();
    this._db = db;
    this._pathPrefix = options.pathPrefix ?? 'data/';
    this._auth = options.auth || null;
  }

  _getRef(path) {
    return this._db.ref(this._pathPrefix + path);
  }

  async get(path) {
    const snapshot = await this._getRef(path).once('value');
    return snapshot.exists() ? snapshot.val() : null;
  }

  async set(path, data) {
    await this._getRef(path).set(data);
  }

  async update(path, partialData) {
    await this._getRef(path).update(partialData);
  }

  async push(path, data) {
    const newRef = this._getRef(path).push();
    await newRef.set(data);
    return newRef.key;
  }

  async remove(path) {
    await this._getRef(path).remove();
  }

  async list(path) {
    const snapshot = await this._getRef(path).once('value');
    if (!snapshot.exists()) return [];
    const val = snapshot.val();
    if (Array.isArray(val)) return val;
    return Object.entries(val).map(([key, value]) => ({
      ...(typeof value === 'object' && value !== null ? value : { _value: value }),
      _key: key,
    }));
  }

  subscribe(path, callback) {
    const dbRef = this._getRef(path);
    const handler = (snapshot) => {
      callback(snapshot.exists() ? snapshot.val() : null);
    };
    dbRef.on('value', handler);
    return () => dbRef.off('value', handler);
  }

  // Equality query on one child (orderByChild + equalTo). Security rules only
  // let non-support users read chats and tickets through these queries.
  _queryRef(path, child, value) {
    return this._getRef(path).orderByChild(child).equalTo(value);
  }

  async listByChild(path, child, value) {
    const snapshot = await this._queryRef(path, child, value).once('value');
    if (!snapshot.exists()) return [];
    return Object.entries(snapshot.val())
      .filter(([, item]) => item !== null && item !== undefined)
      .map(([key, item]) => ({
        ...(typeof item === 'object' ? item : { _value: item }),
        _key: key,
      }));
  }

  subscribeByChild(path, child, value, callback) {
    const query = this._queryRef(path, child, value);
    const handler = (snapshot) => {
      callback(snapshot.exists() ? snapshot.val() : null);
    };
    query.on('value', handler);
    return () => query.off('value', handler);
  }

  // Custom claims of the signed-in user ({ userId, tenantId, role }).
  // These are the values the database rules check, so record scoping uses them.
  async getAuthScope() {
    const firebaseUser = (this._auth || window.firebaseAuth)?.currentUser;
    if (!firebaseUser) return null;
    const { claims } = await firebaseUser.getIdTokenResult();
    return {
      userId: claims.userId || null,
      tenantId: claims.tenantId || null,
      role: claims.role || null,
    };
  }

  serverTimestamp() {
    // compat mode server timestamp sentinel
    return window.firebase?.database?.ServerValue?.TIMESTAMP || Date.now();
  }
}

export default FirebaseCompatAdapter;
